import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { matchPath } from 'react-router-dom'
import ModalContext from 'contexts/ModalContext'
import MasterModalContext from 'contexts/MasterModalContext'
import { useAppSelector } from 'hooks/reduxHooks'
import type { LuxPlusInvalidCard } from 'luxPlus/components/LuxPlusInvalidCardModal/LuxPlusInvalidCardModal'
import LuxPlusInvalidCardModal from 'luxPlus/components/LuxPlusInvalidCardModal/LuxPlusInvalidCardModal'
import useLuxPlusSubscriptionRenewalPaymentCards from 'luxPlus/hooks/useLuxPlusSubscriptionRenewalPaymentCards'
import { recordLuxPlusInvalidCardModalView } from 'luxPlus/lib/luxPlusInvalidCardModalViews'
import getAreAllLuxPlusRenewalCardsInvalid from 'luxPlus/selectors/getAreAllLuxPlusRenewalCardsInvalid'

export const LUX_PLUS_INVALID_CARD_MODAL_EXCLUDED_ROUTES = [
  '/:regionCode/account',
  '/:regionCode/checkout',
  '/:regionCode/checkout-restore-cart',
  '/:regionCode/checkout-gateway',
  '/:regionCode/trip-planner',
  '/:regionCode/lux-plus',
  '/:regionCode/lux-plus-terms-and-conditions',
]

const MODAL_OPENED_TIMEOUT_IN_SECONDS = 10

function isLuxPlusInvalidCard(card: App.LuxPlusSubscriptionRenewalPaymentCard): card is LuxPlusInvalidCard {
  return card.isInvalid && !!card.card
}

/**
 * Opens the LuxPlus+ invalid card modal when every LP+ renewal card has been flagged as likely to fail.
 * Shows at most once every LUX_PLUS_INVALID_CARD_MODAL_COOLDOWN_HOURS (shared across tabs) and at most
 * MAX_LUX_PLUS_INVALID_CARD_MODAL_VIEWS times for the same set of invalid cards (see luxPlusInvalidCardModalViews).
 * Never shows on excluded routes or on top of another modal, and skips when storage is unavailable.
 */
function useShowLuxPlusInvalidCardModal() {
  const [modalRecentlyOpened, setModalRecentlyOpened] = useState(false)
  const hasShownRef = useRef(false)
  const showModal = useContext(ModalContext)
  const modalState = useContext(MasterModalContext)

  const memberId = useAppSelector(state => state.auth.account.memberId)
  const subscription = useAppSelector(state => state.auth.account.luxPlus.member.subscription.item)
  const currentPath = useAppSelector(state => state.router.location.pathname)
  const isEligibleMember = !!memberId && subscription?.status === 'ACTIVE' && subscription.renewalStatus === 'ALLOW_RENEWAL'
  const renewalPaymentCards = useLuxPlusSubscriptionRenewalPaymentCards(isEligibleMember)
  const areAllCardsInvalid = useAppSelector(getAreAllLuxPlusRenewalCardsInvalid)
  const isExcludedRoute = LUX_PLUS_INVALID_CARD_MODAL_EXCLUDED_ROUTES.some(path => matchPath({ path, end: false }, currentPath))

  const invalidCards = useMemo(() => {
    const cards = renewalPaymentCards?.data?.cards ?? []
    return cards.filter(isLuxPlusInvalidCard).sort((left, right) => left.priority - right.priority)
  }, [renewalPaymentCards?.data?.cards])

  // Prevent opening over other modals
  useEffect(() => {
    let openOtherModalTimeout: number

    if (modalState.open) {
      setModalRecentlyOpened(true)
    }

    if (!modalState.open && modalRecentlyOpened) {
      openOtherModalTimeout = window.setTimeout(() => {
        setModalRecentlyOpened(false)
      }, MODAL_OPENED_TIMEOUT_IN_SECONDS * 1000)
    }

    return () => {
      clearTimeout(openOtherModalTimeout)
    }
  }, [modalState.open, modalRecentlyOpened])

  const triggerDisplay = isEligibleMember && areAllCardsInvalid && invalidCards.length > 0 && !isExcludedRoute && !modalRecentlyOpened && !modalState.open

  useEffect(() => {
    if (!triggerDisplay || !memberId || hasShownRef.current) return

    let shouldShow = false
    try {
      shouldShow = recordLuxPlusInvalidCardModalView(memberId, invalidCards.map(card => card.paymentMethod))
    } catch {
      // Storage unavailable (private browsing, quota exceeded): we can't limit views, so don't show
      shouldShow = false
    }
    if (!shouldShow) return

    hasShownRef.current = true
    showModal(<LuxPlusInvalidCardModal invalidCards={invalidCards} />)
  }, [invalidCards, memberId, showModal, triggerDisplay])
}

export default useShowLuxPlusInvalidCardModal
